import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBlogPostBySlug, fetchBlogPosts } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';
import MediaEmbed from '../components/MediaEmbed';
import ShareButtons from '../components/ShareButtons';
import GalleryModal from '../components/GalleryModal';
import ScrollReveal from '../components/ScrollReveal';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Lightbox state for post images
  const [lightboxImages, setLightboxImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  // Track scroll progress for the top reading bar
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    async function loadPost() {
      setLoading(true);
      setError(null);
      try {
        // Fetch all blog posts for reliable lookup, related posts and prev/next nav
        const allPostsRes = await fetchBlogPosts('sort=date&order=desc');
        const postsList = allPostsRes.items || [];
        setAllPosts(postsList);

        // Try direct slug API first
        let currentPost = null;
        try {
          const directRes = await fetchBlogPostBySlug(slug);
          if (directRes && (directRes.title || directRes.content)) {
            currentPost = directRes;
          }
        } catch {
          // fallback to list search below
        }

        // Fallback: lookup in postsList by slug or id
        if (!currentPost) {
          currentPost = postsList.find((p) => {
            const pSlug = p.slug?.da || p.slug;
            return pSlug === slug || p.id === slug;
          });
        }

        if (!currentPost) {
          setError('Blogindlægget blev ikke fundet.');
        } else {
          setPost(currentPost);
        }
      } catch (err) {
        console.error('Fejl ved hentning af blogindlæg:', err);
        setError('Der opstod en fejl under hentning af blogindlægget.');
      } finally {
        setLoading(false);
      }
    }
    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[75vh] items-center justify-center bg-[#FAF8F5]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-3 border-clay-600 border-t-transparent" />
          <span className="text-xs uppercase tracking-widest text-foreground/60 font-medium">
            Henter artiklen...
          </span>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center bg-[#FAF8F5] px-6 py-20">
        <div className="text-center max-w-md bg-white p-8 md:p-12 rounded-3xl border border-sand-200 shadow-sm">
          <div className="w-16 h-16 rounded-full bg-sand-100 flex items-center justify-center mx-auto mb-4 text-clay-700">
            <i className="fa-solid fa-file-circle-question text-2xl" aria-hidden="true" />
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-stone-900 mb-3">
            Artiklen blev ikke fundet
          </h1>
          <p className="text-foreground/75 text-sm mb-6 leading-relaxed">
            Det efterspurgte blogindlæg findes ikke længere eller er blevet flyttet.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay-800 text-white text-xs uppercase tracking-widest font-bold hover:bg-clay-900 transition-all shadow-xs"
          >
            <i className="fa-solid fa-arrow-left text-xs" />
            <span>Tilbage til bloggen</span>
          </Link>
        </div>
      </div>
    );
  }

  const title = post.title?.da || post.title || 'Uden titel';
  const summary = post.summary?.da || post.summary || '';
  const contentHtml = post.content?.da || post.content || '';
  const author = post.author || 'Heidi Svelmøe';
  const mainImage = post.image?.[0] || null;
  const galleryImages = post.gallery || [];
  const videoUrl = post.video_url || null;
  const videoFile = post.video_file || null;
  const hasVideo = Boolean(videoUrl || videoFile);

  const allDisplayImages = [
    ...(mainImage ? [mainImage] : []),
    ...galleryImages,
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  // Estimate reading time removed

  const openLightbox = (index) => {
    setLightboxImages(allDisplayImages);
    setActiveImageIndex(index);
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Prev / Next post navigation
  const currentIndex = allPosts.findIndex((p) => {
    const pSlug = p.slug?.da || p.slug;
    return pSlug === slug || p.id === post.id;
  });
  const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
  const nextPost = currentIndex >= 0 && currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;

  // Other related posts (excluding current)
  const otherPosts = allPosts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner - Matches site aesthetic perfectly */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={getOptimizedImageUrl(mainImage || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede-til-siden-bio.jpg', { width: 1920, quality: 60 })}
            alt="Baggrund"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-white/90">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-white underline underline-offset-4 mr-1 font-medium"
            >
              <i className="fa-solid fa-arrow-left text-[11px]" />
              Alle blogindlæg
            </Link>
            <span>•</span>
            <span>{formatDate(post.date)}</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide leading-tight text-white">
            {title}
          </h1>

          <div className="flex items-center justify-center gap-3 pt-2">
            <img
              src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/heidi-svelmoe-portraet.webp', { width: 80, height: 80, quality: 85 })}
              alt="Heidi Svelmøe"
              className="w-9 h-9 rounded-full object-cover border border-white/40 shadow-sm"
            />
            <span className="text-xs sm:text-sm font-medium text-white">
              Af {author}
            </span>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
            
            {/* Left Column: Media / Portrait / Sharing (5 Cols, Sticky) */}
            <div className="md:col-span-5 md:sticky md:top-36 xl:md:top-40 space-y-6">
              {hasVideo ? (
                <div className="flex flex-col items-center">
                  <div className="w-full">
                    <MediaEmbed
                      videoUrl={videoUrl}
                      videoFile={videoFile}
                      poster={mainImage}
                      title={title}
                    />
                  </div>
                </div>
              ) : mainImage ? (
                <div
                  className="relative group overflow-hidden shadow-lg cursor-pointer bg-sand-200"
                  onClick={() => openLightbox(0)}
                  title="Klik for at se billedet i fuld størrelse"
                >
                  <img
                    src={getOptimizedImageUrl(mainImage, { width: 900, quality: 85 })}
                    alt={title}
                    className="w-full object-cover object-top aspect-[4/3] group-hover:scale-102 transition-transform duration-500"
                  />
                  <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="fa-solid fa-magnifying-glass-plus text-xs" />
                    <span>Forstør</span>
                  </div>
                </div>
              ) : null}

              {/* Quick Info & Share Box */}
              <div className="bg-white p-6 border border-sand-200/80 shadow-xs">
                <span className="text-xs uppercase tracking-wider text-sage-600 font-semibold block mb-2">
                  Om artiklen
                </span>
                <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                  Skrevet af Heidi Svelmøe med inspiration fra hverdagen i Solsalen og musikscenen.
                </p>

                {post.terms?.kategori && post.terms.kategori.length > 0 && (
                  <div className="mb-5 pt-3 border-t border-sand-100">
                    <span className="text-xs text-foreground/60 block mb-2 font-medium">Kategorier:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {post.terms.kategori.map((k) => (
                        <Link
                          key={k}
                          to={`/blog?kategori=${k}`}
                          className="text-xs px-2.5 py-1 bg-sand-100 hover:bg-sand-200 text-foreground/80 transition-colors"
                        >
                          {k.replace('-', ' ')}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t border-sand-100">
                  <span className="text-xs text-foreground/60 block mb-2 font-medium">Del dette indlæg:</span>
                  <ShareButtons
                    url={currentUrl}
                    title={title}
                    summary={summary}
                    image={mainImage}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Article Text & Body (7 Cols) */}
            <div className="md:col-span-7 bg-white p-8 sm:p-10 md:p-12 border border-sand-200/80 shadow-xs">
              {summary && summary.trim().replace(/\s+/g, ' ') !== contentHtml.replace(/<[^>]*>/g, '').trim().replace(/\s+/g, ' ') && (
                <p className="text-lg sm:text-xl font-serif italic text-stone-700 leading-relaxed pb-6 mb-8 border-b border-sand-200">
                  {summary}
                </p>
              )}

              <div
                className="prose prose-stone max-w-none
                  text-stone-700 text-base sm:text-lg leading-relaxed
                  prose-headings:font-serif prose-headings:font-normal prose-headings:text-black
                  prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:mb-5 prose-p:leading-relaxed
                  prose-strong:font-semibold prose-strong:text-stone-900
                  prose-em:italic
                  prose-ul:my-6 prose-ul:space-y-3 prose-ul:pl-2
                  [&_ul]:list-none
                  [&_ul_li]:relative [&_ul_li]:pl-6 [&_ul_li]:before:content-['•'] [&_ul_li]:before:absolute [&_ul_li]:before:left-1 [&_ul_li]:before:text-clay-600 [&_ul_li]:before:font-bold
                  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-6 [&_ol]:space-y-3
                  [&_li]:leading-relaxed
                  prose-blockquote:border-l-2 prose-blockquote:border-clay-500 prose-blockquote:pl-5 prose-blockquote:my-6 prose-blockquote:italic prose-blockquote:text-stone-600
                  prose-a:text-clay-700 prose-a:underline hover:prose-a:text-black transition-colors"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />

              {/* Photo Gallery if extra images exist */}
              {galleryImages.length > 0 && (
                <div className="mt-12 pt-8 border-t border-sand-200">
                  <h3 className="font-serif text-2xl font-normal text-black mb-4">
                    Galleri fra artiklen
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {galleryImages.map((imgUrl, idx) => {
                      const globalIndex = mainImage ? idx + 1 : idx;
                      return (
                        <button
                          key={imgUrl}
                          type="button"
                          onClick={() => openLightbox(globalIndex)}
                          className="aspect-square overflow-hidden bg-sand-100 border border-sand-200 shadow-xs hover:shadow-md group focus:outline-none transition-all cursor-pointer"
                        >
                          <img
                            src={getOptimizedImageUrl(imgUrl, { width: 400, height: 400, quality: 85 })}
                            alt={`Galleri billede ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prev / Next Post Links */}
              <div className="mt-12 pt-8 border-t border-sand-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                {prevPost ? (
                  <Link
                    to={`/blog/${prevPost.slug?.da || prevPost.slug || prevPost.id}`}
                    className="inline-flex items-center gap-2 text-sm text-clay-700 hover:text-black font-medium"
                  >
                    <i className="fa-solid fa-arrow-left text-xs" />
                    <span>Forrige: {prevPost.title?.da || prevPost.title}</span>
                  </Link>
                ) : <span />}

                {nextPost ? (
                  <Link
                    to={`/blog/${nextPost.slug?.da || nextPost.slug || nextPost.id}`}
                    className="inline-flex items-center gap-2 text-sm text-clay-700 hover:text-black font-medium"
                  >
                    <span>Næste: {nextPost.title?.da || nextPost.title}</span>
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                ) : <span />}
              </div>

              <div className="mt-8">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-sm font-semibold tracking-wider uppercase text-clay-800 hover:text-black transition-colors"
                >
                  <i className="fa-solid fa-arrow-left text-xs" />
                  <span>Se alle blogindlæg</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Relaterede artikler */}
      {otherPosts.length > 0 && (
        <section className="py-16 bg-white border-t border-sand-200">
          <div className="mx-auto max-w-7xl px-6">
            <div className="flex items-center justify-between mb-8 pb-3 border-b border-sand-200">
              <h3 className="font-serif text-2xl sm:text-3xl font-normal text-black">
                Flere artikler fra bloggen
              </h3>
              <Link
                to="/blog"
                className="text-xs uppercase tracking-widest font-semibold text-clay-800 hover:text-black transition-colors inline-flex items-center gap-1.5"
              >
                <span>Se alle</span>
                <i className="fa-solid fa-arrow-right text-[10px]" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {otherPosts.map((p) => {
                const pSlug = p.slug?.da || p.slug || p.id;
                const pTitle = p.title?.da || p.title;
                const pImg = p.image?.[0] || p.gallery?.[0] || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede.jpg';

                return (
                  <article key={p.id} className="h-full bg-sand-50 border border-sand-200 overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col group">
                    <div className="aspect-[16/10] overflow-hidden bg-sand-200">
                      <Link to={`/blog/${pSlug}`} className="block w-full h-full">
                        <img
                          src={getOptimizedImageUrl(pImg, { width: 650, quality: 85 })}
                          alt={pTitle}
                          className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                        />
                      </Link>
                    </div>
                    <div className="p-6 flex-grow flex flex-col justify-between">
                      <div>
                        <span className="text-xs text-foreground/60 mb-2 block">
                          {formatDate(p.date)}
                        </span>
                        <h4 className="font-serif text-lg sm:text-xl font-bold text-stone-900 mb-2 leading-snug group-hover:text-clay-700 transition-colors line-clamp-2">
                          <Link to={`/blog/${pSlug}`}>
                            {pTitle}
                          </Link>
                        </h4>
                        <p className="text-xs sm:text-sm text-stone-700 line-clamp-3 leading-relaxed font-normal">
                          {p.summary?.da || p.summary}
                        </p>
                      </div>
                      <div className="mt-5 pt-3 border-t border-sand-200">
                        <Link
                          to={`/blog/${pSlug}`}
                          className="text-xs uppercase tracking-wider font-semibold text-clay-800 hover:text-black transition-colors inline-flex items-center gap-1.5"
                        >
                          <span>Læs indlæg</span>
                          <i className="fa-solid fa-arrow-right text-[10px]" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {activeImageIndex !== null && lightboxImages.length > 0 && (
        <GalleryModal
          images={lightboxImages}
          currentIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
          onNext={() => setActiveImageIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
        />
      )}
    </div>
  );
}
